import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  deleteIntegration,
  saveOrUpdateIntegration,
  updateIntegrationStatus,
  getActiveWebHook,
  fetchIntegrationByUuid,
} from '../../api/IntegrationApi';
import { Agent, Integration, IntegrationStatus, ChannelType } from '../model';
import './IntegrationPage.scss';
import { fetchAllAgents } from '../../api/AgentApi';
import { PageProps } from '../main/App';
import IntegrationForm from './IntegrationForm';
import IntegrationWebhookInfo from './IntegrationWebhookInfo';

const emptyIntegration: Integration = {
  name: '',
  botToken: '',
  type: ChannelType.TELEGRAM,
  status: IntegrationStatus.INACTIVE,
};

const useIntegrationData = (uuid?: string) => {
  const [integration, setIntegration] = useState<Integration | null>(null);
  const [originalIntegration, setOriginalIntegration] =
    useState<Integration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [webHook, setWebHook] = useState<string | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const isNewIntegration = !uuid;

  useEffect(() => {
    fetchAllAgents()
      .then(setAgents)
      .catch(() => setError('Failed to load agents'));
  }, []);

  useEffect(() => {
    if (!isNewIntegration) {
      fetchIntegrationByUuid(uuid)
        .then(data => {
          setIntegration(data);
          setOriginalIntegration({ ...data });
        })
        .catch(() => setError('Failed to load integration'));
    } else {
      setIntegration(emptyIntegration);
      setOriginalIntegration(emptyIntegration);
    }
  }, [uuid, isNewIntegration]);

  useEffect(() => {
    if (
      integration?.status === IntegrationStatus.ACTIVE &&
      integration?.type === ChannelType.TELEGRAM &&
      uuid
    ) {
      getActiveWebHook(uuid)
        .then(webhookUrl => setWebHook(JSON.stringify(webhookUrl.result)))
        .catch(() => setError('Failed to load webhook URL'));
    }
  }, [integration?.status, integration?.type, uuid]);

  return {
    integration,
    setIntegration,
    originalIntegration,
    setOriginalIntegration,
    error,
    setError,
    webHook,
    agents,
    isNewIntegration,
  };
};

const IntegrationPage: React.FC<PageProps> = ({ showNotification }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const {
    integration,
    setIntegration,
    originalIntegration,
    setOriginalIntegration,
    error,
    setError,
    webHook,
    agents,
    isNewIntegration,
  } = useIntegrationData(uuid);

  const isIntegrationChanged =
    JSON.stringify(integration) !== JSON.stringify(originalIntegration);
  const cannotBeActivated =
    integration?.status === IntegrationStatus.INACTIVE && isIntegrationChanged;
  const isSaveDisabled =
    !integration ||
    !isIntegrationChanged ||
    integration.status === IntegrationStatus.ACTIVE;
  const isDeleteDisabled = integration?.status === IntegrationStatus.ACTIVE;

  const validateIntegration = useCallback(() => {
    if (
      !integration?.name ||
      !integration?.agentUuid ||
      (integration.type === ChannelType.TELEGRAM && !integration?.botToken) ||
      (integration.type === ChannelType.WEBHOOK &&
        !integration?.responseWebhook)
    ) {
      showNotification(
        'Please fill in all required fields: Name, Bot Token, and Agent'
      );
      return false;
    }
    return true;
  }, [integration, showNotification]);

  const handleSave = useCallback(async () => {
    if (!validateIntegration()) return;
    try {
      if (integration) {
        const savedIntegration = await saveOrUpdateIntegration(integration);
        setIntegration(savedIntegration);
        setOriginalIntegration(savedIntegration);
        navigate(`/integration/${savedIntegration.uuid}`);
        setError(null);
      }
    } catch {
      setError('Failed to save integration');
    }
  }, [
    integration,
    setIntegration,
    setOriginalIntegration,
    navigate,
    setError,
    validateIntegration,
  ]);

  const handleToggleStatus = useCallback(async () => {
    if (cannotBeActivated) {
      showNotification('You must save the integration before activating!');
      return;
    }
    try {
      if (integration) {
        const newStatus =
          integration.status === IntegrationStatus.ACTIVE
            ? IntegrationStatus.INACTIVE
            : IntegrationStatus.ACTIVE;
        await updateIntegrationStatus(uuid!, newStatus);
        setOriginalIntegration({ ...integration, status: newStatus });
        setIntegration({ ...integration, status: newStatus });
      }
    } catch {
      setError('Failed to update integration status');
    }
  }, [
    integration,
    cannotBeActivated,
    setIntegration,
    setError,
    showNotification,
    uuid,
  ]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteIntegration(uuid!);
      navigate('/integrations');
    } catch {
      setError('Failed to delete integration');
    }
  }, [uuid, navigate, setError]);

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          showNotification('Secret Token copied to clipboard!');
        })
        .catch(() => {
          showNotification('Failed to copy Secret Token');
        });
    },
    [showNotification]
  );

  if (integration === null) return <div className="loading">Loading...</div>;

  return (
    <div className="integration-page">
      <IntegrationForm
        integration={integration}
        agents={agents}
        isNewIntegration={isNewIntegration}
        onIntegrationChange={setIntegration}
        onSave={handleSave}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
        isSaveDisabled={isSaveDisabled}
        isDeleteDisabled={isDeleteDisabled}
        isStatusToggleDisabled={cannotBeActivated}
      />

      {error && <div className="error-message">{error}</div>}

      <IntegrationWebhookInfo
        integration={integration}
        webHook={webHook}
        onCopyToClipboard={copyToClipboard}
      />
    </div>
  );
};

export default IntegrationPage;
